import { GroupByOptionsBrandEquityMetric } from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { GeoMMMInfoRow } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import { RMMMScenarioPlanningModel } from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import { ScenarioPlanningModelExt } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';
import { formatRunLabels, MmmKpi } from '../DtcCommerceMMM/utils/dtc-commerce-mmm-runs-discovery';
import { FormattedModelRunOptions, formatSegmentLabel, normalizeSegment } from '../mmm-utils';
import {
  INLINE_RUNS,
  KpiRuns,
  ModelRun,
  ReleaseStatus,
  runKey
} from './mmm-run-selector-constants';

export interface RunGroup {
  inline: ModelRun[];
  hidden: ModelRun[];
  selectedIsHidden: boolean;
}

/**
 * Split one release group of a KPI's runs (newest first) into the rows the run menu always lists
 * (the newest INLINE_RUNS) and the older runs behind that group's disclosure row. When the
 * selected run is one of the hidden ones, the menu opens with that group expanded.
 */
export const splitRunGroup = (runs: ModelRun[], selectedDate: string): RunGroup => {
  const hidden = runs.slice(INLINE_RUNS);
  return {
    inline: runs.slice(0, INLINE_RUNS),
    hidden,
    selectedIsHidden: hidden.some(run => runKey(run) === selectedDate)
  };
};

/**
 * Pivot the date-first run list (one entry per run, KPIs nested) into a KPI-first structure
 * (one entry per KPI, runs nested newest-first) — the core re-pivot behind DEV-23639.
 * Non-internal callers only ever see client-facing runs.
 */
export const toModelRuns = (
  modelRunOptions: FormattedModelRunOptions[],
  { isInternal }: { isInternal: boolean }
): KpiRuns[] => {
  const byKpi = new Map<string, { label: string; runs: Omit<ModelRun, 'label'>[] }>();

  modelRunOptions.forEach(run => {
    run.kpis.forEach(kpiOption => {
      if (!isInternal && !kpiOption.client_facing) return;
      const group = byKpi.get(kpiOption.value) ?? { label: kpiOption.label, runs: [] };
      group.runs.push({
        kpi: kpiOption.value,
        branch: run.branch,
        refreshDate: run.date,
        releaseStatus: kpiOption.client_facing
          ? ReleaseStatus.CLIENT_FACING
          : ReleaseStatus.UNMARKED,
        isCurrent: false
      });
      byKpi.set(kpiOption.value, group);
    });
  });

  const kpiRuns: KpiRuns[] = [];
  byKpi.forEach((group, value) => {
    const sorted = group.runs
      .slice()
      .sort((a, b) => Date.parse(b.refreshDate) - Date.parse(a.refreshDate));
    const labels = formatRunLabels(sorted.map(run => run.refreshDate));
    const runs: ModelRun[] = sorted.map((run, i) => ({ ...run, label: labels[i] }));

    // The newest client-facing run for this KPI is "current" — mirrors MmmVariantPage's own
    // initial-selection rule (newest client-facing run, then that run's client-facing KPI).
    const current = runs.find(run => run.releaseStatus === ReleaseStatus.CLIENT_FACING);
    if (current) {
      current.isCurrent = true;
    }

    kpiRuns.push({ value, label: group.label, runs });
  });

  return kpiRuns.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * The DTC/Commerce MMM pages' run discovery is already KPI-first with runs newest-first, so
 * this only renames fields and marks each KPI's newest client-facing run as current.
 */
export const discoveryToModelRuns = (kpis: MmmKpi[]): KpiRuns[] =>
  kpis.map(({ name, runs }) => {
    const currentTimestamp = runs.find(run => run.clientFacing)?.timestamp;
    return {
      value: name,
      label: name,
      runs: runs.map(run => ({
        kpi: name,
        branch: run.branch,
        refreshDate: run.timestamp,
        releaseStatus: run.clientFacing ? ReleaseStatus.CLIENT_FACING : ReleaseStatus.UNMARKED,
        isCurrent: run.timestamp === currentTimestamp,
        label: run.label
      }))
    };
  });

// Geo MMM runs are keyed by KPI + outcome, so that pair is the selector's "KPI".
export const geoKpiKey = (row: GeoMMMInfoRow): string =>
  `${normalizeSegment(row.kpi)} - ${normalizeSegment(row.outcome)}`;

/**
 * Pivot Geo MMM's flat run list (/getGeoMMMInfo) into KPI + outcome groups, runs newest-first
 * (modelRunDate is an ISO date). Geo runs carry no branch, so no branch badge shows.
 */
export const geoToModelRuns = (rows: GeoMMMInfoRow[]): KpiRuns[] => {
  const byKey = new Map<string, GeoMMMInfoRow[]>();
  rows.forEach(row => byKey.set(geoKpiKey(row), [...(byKey.get(geoKpiKey(row)) ?? []), row]));

  return Array.from(byKey.entries())
    .map(([value, group]) => {
      const sorted = group.slice().sort((a, b) => b.modelRunDate.localeCompare(a.modelRunDate));
      const current = sorted.find(row => row.clientFacing);
      const labels = formatRunLabels(sorted.map(row => row.modelRunDate));
      return {
        value,
        label: `${formatSegmentLabel(group[0].kpi)} — ${formatSegmentLabel(group[0].outcome)}`,
        runs: sorted.map((row, i) => ({
          kpi: value,
          branch: '',
          refreshDate: row.modelRunDate,
          releaseStatus: row.clientFacing ? ReleaseStatus.CLIENT_FACING : ReleaseStatus.UNMARKED,
          isCurrent: row === current,
          label: labels[i]
        }))
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Brand Equity has one run list for every brand metric (the metric is chosen elsewhere on the
// page), so its runs form a single selector group.
export const BRAND_EQUITY_RUN_KPI = 'brand_equity';

/**
 * Pivot Brand Equity's runs, keyed by run date (or the literal `latest`), into one run group,
 * `latest` first then newest-first. A run is client-facing when any of its metrics is; external
 * users only see those. Its branch is the first metric's, shown on non-client-facing runs.
 */
export const brandEquityToModelRuns = (
  options: Record<string, GroupByOptionsBrandEquityMetric[]>,
  { isInternal }: { isInternal: boolean }
): KpiRuns[] => {
  const dateKeys = Object.keys(options)
    .filter(dateKey => isInternal || options[dateKey].some(item => item.client_facing))
    .sort((a, b) => {
      if (a === 'latest' || b === 'latest') return a === 'latest' ? -1 : 1;
      return Date.parse(b) - Date.parse(a);
    });
  if (dateKeys.length === 0) return [];

  const concreteKeys = dateKeys.filter(dateKey => dateKey !== 'latest');
  const labels = formatRunLabels(concreteKeys);
  const current = dateKeys.find(dateKey => options[dateKey].some(item => item.client_facing));
  const runs: ModelRun[] = dateKeys.map(dateKey => {
    const clientFacing = options[dateKey].some(item => item.client_facing);
    return {
      kpi: BRAND_EQUITY_RUN_KPI,
      branch: options[dateKey][0]?.branch ?? '',
      refreshDate: dateKey,
      releaseStatus: clientFacing ? ReleaseStatus.CLIENT_FACING : ReleaseStatus.UNMARKED,
      isCurrent: dateKey === current,
      label: dateKey === 'latest' ? 'Latest' : labels[concreteKeys.indexOf(dateKey)]
    };
  });
  return [{ value: BRAND_EQUITY_RUN_KPI, label: 'Brand Equity', runs }];
};

export const NO_SCENARIO_MODEL_REASON = 'This run has no scenario planning model.';

/**
 * On the Scenario Planning tab the page's run picker shows every run, but one with no scenario
 * model can't be planned on: mark it disabled with a reason. `hasModel` says whether a run has one.
 */
export const markRunsWithoutScenarioModel = (
  kpiRuns: KpiRuns[],
  hasModel: (run: ModelRun) => boolean
): KpiRuns[] =>
  kpiRuns.map(group => ({
    ...group,
    runs: group.runs.map(run =>
      hasModel(run) ? run : { ...run, disabledReason: NO_SCENARIO_MODEL_REASON }
    )
  }));

// The lambda only attaches a model to an rMMM run that has its scenario artifacts, so a model that
// arrives on a run row is always usable.
export const rmmmRowModelToSpModel = (
  model: RMMMScenarioPlanningModel
): ScenarioPlanningModelExt => ({
  ...model,
  hasScenarioArtifacts: true
});
